import { format } from 'date-fns'
import { CheckinCycle, Recurrence } from './types'

const RECURRENCE_LABELS: Record<Recurrence, string> = {
  one_time: 'One-time',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  bi_annually: 'Bi-annually',
  annually: 'Annually',
}

export const recurrenceLabel = (recurrence: Recurrence): string =>
  RECURRENCE_LABELS[recurrence] ?? recurrence

// Parse a `YYYY-MM-DD` string into a local Date without the UTC shift that
// `new Date('2026-07-01')` introduces (which would render as the prior day in
// negative-offset timezones).
const parseLocalDate = (value: string | null): Date | null => {
  if (!value) return null
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return null
  return new Date(year, month - 1, day)
}

const formatDate = (date: Date): string => format(date, 'MM/dd/yyyy')

// A recurring cycle is scheduled by its recurrence dates; a one-time cycle by
// its base dates.
const scheduleDates = (
  cycle: CheckinCycle,
): { start: string | null; end: string | null } =>
  cycle.recurrence === 'one_time'
    ? { start: cycle.start_date, end: cycle.end_date }
    : { start: cycle.rec_start_date, end: cycle.rec_end_date }

// Format a raw `YYYY-MM-DD` start/end pair as "07/01/2026 – 07/31/2026"
// (used by the review step, which works from form values, not a saved cycle).
export const formatDayRange = (
  start: string | null,
  end: string | null,
): string => {
  const startDate = parseLocalDate(start)
  const endDate = parseLocalDate(end)

  if (!startDate) return ''
  if (!endDate) return formatDate(startDate)

  return `${formatDate(startDate)} – ${formatDate(endDate)}`
}

// e.g. "07/01/2026 – 07/31/2026", or just the start when there is no end.
export const formatDateRange = (cycle: CheckinCycle): string => {
  const { start, end } = scheduleDates(cycle)
  const startDate = parseLocalDate(start)
  const endDate = parseLocalDate(end)

  if (!startDate) return ''
  if (!endDate) return formatDate(startDate)

  return `${formatDate(startDate)} – ${formatDate(endDate)}`
}

export type CycleStatus = 'active' | 'scheduled' | 'archived'

// Archived cycles read as "Archived"; a cycle whose start is still in the future
// is "Scheduled"; anything that has already started is "Active".
export const cycleStatus = (cycle: CheckinCycle): CycleStatus => {
  if (cycle.archived) return 'archived'

  const { start } = scheduleDates(cycle)
  const startDate = parseLocalDate(start)
  if (!startDate) return 'active'

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return startDate > today ? 'scheduled' : 'active'
}
